<template>
  <div class="main row">
    <div class="col-md-12">
      <KTCodePreview v-bind:title="'Import Phiếu yêu cầu điều hàng'">
        <template v-slot:preview>
          <!-- ===== Bước 1 + 2: template & upload ===== -->
          <b-overlay :show="isProcessing" rounded opacity="0.6" spinner-small>
            <b-row class="mb-3">
              <b-col>
                <div class="form-group mb-2">
                  <span>
                    <b>Bước 1:</b> Vui lòng tải file Excel mẫu đúng format
                    <a :href="urlTemplate">tại đây</a>
                  </span>
                </div>
                <span class="d-block mb-2">
                  <b>Bước 2:</b> Tải file Excel để hệ thống đọc &amp; xem trước
                </span>
                <UploadFileMulter
                  :cmdUrl="'v2/stock-transfer-imports/transform'"
                  v-on:send-file="onTransformedExcel"
                  v-on:clear-file="onClearExcel"
                  v-on:pick-file="onClearExcel"
                  v-on:upload-state="onUploadState"
                />
                <span v-if="fileName" class="d-block mt-2">
                  Tên file: <b>{{ fileName }}</b>
                </span>
                <b-alert show variant="light" class="mt-2 mb-0" style="font-size: 12px">
                  <i class="fas fa-info-circle"></i>
                  Các dòng có cùng <b>Kho gửi</b> và <b>Kho nhận</b> sẽ được gộp thành
                  <b>một phiếu</b> nhiều sản phẩm. Tối đa 1.000 dòng mỗi file.
                </b-alert>
              </b-col>
            </b-row>
          </b-overlay>

          <!-- ===== Preview ===== -->
          <template v-if="rows.length">
            <!--
              MỘT hàng tiêu đề gom hết: đếm dòng, đếm lỗi, và nút xoá dòng lỗi. Trước đây
              bốn thứ này nằm ở bốn hàng rời (tiêu đề, badge, switch, alert cảnh báo) khiến
              màn hình rất nặng trong khi thông tin thì ít.
            -->
            <b-row class="mb-3">
              <b-col
                class="d-flex align-items-center justify-content-between flex-wrap preview-head"
              >
                <div class="d-flex align-items-center flex-wrap">
                  <strong class="mr-2">{{ rows.length }} dòng</strong>
                  <b-badge variant="success">{{ validRows.length }} hợp lệ</b-badge>
                  <b-badge v-if="errorRows.length" variant="danger" class="ml-1">
                    {{ errorRows.length }} lỗi
                  </b-badge>
                </div>
                <b-button
                  v-if="errorRows.length"
                  size="sm"
                  variant="outline-danger"
                  :disabled="isProcessing"
                  @click="removeAllErrorRows"
                >
                  <i class="fas fa-trash"></i>
                  Xoá {{ errorRows.length }} dòng lỗi
                </b-button>
              </b-col>
            </b-row>

            <!--
              Một dòng nhỏ cho biết file sẽ sinh ra bao nhiêu phiếu. Bảng bên dưới là xem
              trước TỪNG DÒNG EXCEL nên tự nó không cho thấy điều đó — các dòng cùng cặp kho
              gửi–kho nhận sẽ được gộp lại khi tạo.
            -->
            <b-alert
              v-if="groups.length"
              show
              variant="light"
              class="mb-3 group-note"
            >
              <i class="fas fa-file-invoice mr-1"></i>
              Sẽ tạo <strong>{{ groups.length }}</strong> phiếu điều hàng
              <span class="text-muted">(gộp theo cặp kho gửi – kho nhận)</span>
              <span v-if="errorRows.length" class="text-muted">
                — chưa tính các dòng lỗi
              </span>
            </b-alert>

            <b-row class="mb-2">
              <b-col>
                <b-form-checkbox
                  v-model="showOnlyErrors"
                  :disabled="isProcessing || !errorRows.length"
                  switch
                >
                  Chỉ hiện dòng lỗi
                </b-form-checkbox>
              </b-col>
            </b-row>

            <b-row class="mb-3">
              <b-col>
                <b-table
                  :items="pagedRows"
                  :fields="previewFields"
                  :tbody-tr-class="rowClass"
                  bordered
                  hover
                  small
                  responsive
                  class="preview-table"
                >
                  <template #cell(senderStoreName)="row">
                    {{ row.item.senderStoreName || row.item.raw.senderStoreName || '—' }}
                  </template>
                  <template #cell(receiverStoreName)="row">
                    {{
                      row.item.receiverStoreName || row.item.raw.receiverStoreName || '—'
                    }}
                  </template>
                  <template #cell(productCode)="row">
                    {{ row.item.productCode || row.item.raw.productCode || '—' }}
                  </template>
                  <template #cell(productName)="row">
                    {{ row.item.productName || '—' }}
                  </template>
                  <!--
                    Hiện thêm giá trị GỐC khi nó khác giá trị hệ thống đọc ra (vd "1.000"
                    -> 1000). Chỉ hiện số đã xử lý thì mọi biến đổi đều vô hình, người dùng
                    không có cách nào đối chiếu với ô Excel của mình.
                  -->
                  <template #cell(quantity)="row">
                    <template v-if="row.item.quantity === null">
                      {{ row.item.raw.quantity || '—' }}
                    </template>
                    <template v-else>
                      {{ row.item.quantity }}
                      <div
                        v-if="String(row.item.raw.quantity) !== String(row.item.quantity)"
                        class="text-muted raw-hint"
                      >
                        (file: {{ row.item.raw.quantity }})
                      </div>
                    </template>
                  </template>
                  <template #cell(summary)="row">
                    <span class="summary-text">{{ row.item.summary || '—' }}</span>
                  </template>
                  <template #cell(errorMessage)="row">
                    <span v-if="row.item.errorMessage" class="text-danger">
                      <i class="fas fa-exclamation-triangle mr-1"></i>
                      {{ row.item.errorMessage }}
                    </span>
                    <span v-else class="text-success">
                      <i class="fas fa-check"></i>
                    </span>
                  </template>
                  <template #cell(actions)="row">
                    <span
                      class="flaticon2-rubbish-bin-delete-button text-danger"
                      style="cursor: pointer; font-size: 1rem"
                      title="Xoá dòng này"
                      @click="removeRow(row.item)"
                    />
                  </template>
                </b-table>

                <div
                  v-if="filteredRows.length > perPage"
                  class="d-flex align-items-center justify-content-between mt-2 custom-pagination"
                >
                  <div>
                    Hiển thị {{ startRow }}–{{ endRow }} / {{ filteredRows.length }} dòng
                  </div>
                  <b-pagination
                    v-model="currentPage"
                    :total-rows="filteredRows.length"
                    :per-page="perPage"
                    size="sm"
                    align="right"
                    class="mb-0"
                  />
                </div>
              </b-col>
            </b-row>

            <!-- ===== Actions ===== -->
            <b-row>
              <b-col>
                <SaveButton
                  :is-busy="isSubmitting('commit')"
                  :disabled="!canCommit"
                  :button-style="{ fontWeight: '600', minWidth: '210px' }"
                  type="button"
                  @click="onConfirmImport"
                >
                  <i class="fas fa-check mr-1"></i>
                  Xác nhận tạo phiếu
                </SaveButton>
                <b-button
                  variant="secondary"
                  class="ml-2"
                  :disabled="isProcessing"
                  @click="goBack"
                >
                  <i class="fas fa-arrow-left"></i> Quay lại
                </b-button>
              </b-col>
            </b-row>
          </template>

          <b-row v-else>
            <b-col>
              <b-button variant="secondary" :disabled="isProcessing" @click="goBack">
                <i class="fas fa-arrow-left"></i> Quay lại
              </b-button>
            </b-col>
          </b-row>

          <!-- ===== Modal xác nhận ===== -->
          <b-modal
            ref="confirmModal"
            title="Xác nhận tạo phiếu điều hàng"
            ok-title="Xác nhận"
            cancel-title="Huỷ"
            ok-variant="primary"
            @ok="doCommitImport"
          >
            <p class="mb-2">
              Hệ thống sẽ tạo <strong>{{ groups.length }}</strong> phiếu yêu cầu điều hàng
              từ <strong>{{ validRows.length }}</strong> dòng dữ liệu, và
              <strong>gửi thông báo Lark</strong> tới các kho gửi tương ứng.
            </p>
            <p class="mb-0 text-muted">
              Nếu có bất kỳ lỗi nào trong quá trình tạo, toàn bộ sẽ được huỷ bỏ và không
              phiếu nào được tạo.
            </p>
          </b-modal>
        </template>
      </KTCodePreview>
    </div>
  </div>
</template>

<script>
import KTCodePreview from '@/view/content/CodePreview.vue';
import { SET_BREADCRUMB } from '@/core/services/store/modules/breadcrumbs.module';
import ApiService from '@/core/services/api.service';
import { makeToastSuccess, makeToastFaile, handleError } from '@/utils/common';
import UploadFileMulter from '@/view/modules/upload/UploadFileMulter.vue';
import SaveButton from '@/view/components/common/SaveButton.vue';
import submitLock from '@/view/mixins/submit-lock.mixins';
import { BASE_URL } from '@/utils/constants';
import { checkPermissions } from '@/utils/saveDataToLocal';

export default {
  components: {
    KTCodePreview,
    UploadFileMulter,
    SaveButton,
  },
  mixins: [submitLock],
  data() {
    return {
      urlTemplate: BASE_URL + 'templates/download?type=stock-transfer-import',
      fileName: '',
      rows: [],
      isUploading: false,
      showOnlyErrors: false,
      perPage: 20,
      currentPage: 1,
      previewFields: [
        {
          key: 'rowIndex',
          label: '#',
          thStyle: { width: '48px' },
          class: 'text-center',
        },
        { key: 'senderStoreName', label: 'Kho gửi (nguồn)' },
        { key: 'receiverStoreName', label: 'Kho nhận (đích)' },
        { key: 'productCode', label: 'Mã SP' },
        { key: 'productName', label: 'Tên sản phẩm' },
        {
          key: 'quantity',
          label: 'SL',
          thStyle: { width: '70px' },
          class: 'text-center',
        },
        { key: 'summary', label: 'Nội dung' },
        { key: 'errorMessage', label: 'Trạng thái' },
        {
          key: 'actions',
          label: '',
          thStyle: { width: '48px' },
          class: 'text-center',
        },
      ],
    };
  },
  computed: {
    /**
     * Khoá cả nhóm control khi đang commit HOẶC đang tải file mới (Rule 11 mục 3b). Phải phủ
     * cả bước upload vì màn này giữ preview của file TRƯỚC trong lúc file mới đang bay —
     * không khoá thì bấm "Xác nhận tạo phiếu" sẽ tạo phiếu từ dữ liệu file cũ.
     */
    isProcessing() {
      return this.isSubmitting('commit') || this.isUploading;
    },
    validRows() {
      return this.rows.filter((r) => r.isValid);
    },
    errorRows() {
      return this.rows.filter((r) => !r.isValid);
    },
    filteredRows() {
      return this.showOnlyErrors ? this.errorRows : this.rows;
    },
    pagedRows() {
      const start = (this.currentPage - 1) * this.perPage;
      return this.filteredRows.slice(start, start + this.perPage);
    },
    startRow() {
      if (!this.filteredRows.length) return 0;
      return (this.currentPage - 1) * this.perPage + 1;
    },
    endRow() {
      return Math.min(this.currentPage * this.perPage, this.filteredRows.length);
    },
    /**
     * Gom nhóm ngay trên FE thay vì dùng `groups` của BE, vì người dùng được xoá dòng
     * ở màn preview — nhóm phải tính lại theo các dòng CÒN LẠI, và chính mảng này là
     * payload gửi lên `commit`. Khoá nhóm khớp BE: `senderStoreId_receiverStoreId`.
     */
    groups() {
      const map = new Map();
      this.validRows.forEach((row) => {
        const key = `${row.senderStoreId}_${row.receiverStoreId}`;
        if (!map.has(key)) {
          map.set(key, {
            groupKey: key,
            senderStoreId: row.senderStoreId,
            senderStoreName: row.senderStoreName,
            receiverStoreId: row.receiverStoreId,
            receiverStoreName: row.receiverStoreName,
            summary: row.summary,
            products: [],
          });
        }
        map.get(key).products.push({
          productId: row.productId,
          productCode: row.productCode,
          productName: row.productName,
          quantity: Number(row.quantity),
          summary: row.summary,
        });
      });

      return [...map.values()].map((group) => ({
        ...group,
        totalQuantity: group.products.reduce((sum, p) => sum + Number(p.quantity), 0),
      }));
    },
    canCommit() {
      if (this.isProcessing) return false;
      if (!this.validRows.length) return false;
      if (this.errorRows.length) return false;
      return true;
    },
  },
  watch: {
    filteredRows() {
      const maxPage = Math.max(1, Math.ceil(this.filteredRows.length / this.perPage));
      if (this.currentPage > maxPage) this.currentPage = maxPage;
    },
    /**
     * Hết dòng lỗi thì tắt switch "Chỉ hiện dòng lỗi" — nếu không, switch vừa bị
     * `:disabled` vừa còn checked → bảng rỗng và không gạt lại được.
     */
    errorRows(rows) {
      if (!rows.length && this.showOnlyErrors) {
        this.showOnlyErrors = false;
      }
    },
  },
  mounted() {
    //  Route FE không có guard nên gõ thẳng URL vẫn tới được màn; không có quyền thì mọi
    //  request đều 403 — đá về danh sách cho đỡ khó hiểu. Chặn thật ở BE (`TICKET_IMPORT`).
    if (!checkPermissions(['TICKET_IMPORT'])) {
      makeToastFaile('Bạn không có quyền Import phiếu yêu cầu điều hàng!');
      this.$router.push({ name: 'list-ticket' }).catch(() => {});
      return;
    }
    this.$store.dispatch(SET_BREADCRUMB, [
      { title: 'Phiếu yêu cầu hỗ trợ', route: '/ticket' },
      { title: 'Import Yêu cầu điều hàng' },
    ]);
  },
  methods: {
    makeToastSuccess,
    makeToastFaile,
    rowClass(item) {
      if (!item) return '';
      return item.isValid ? '' : 'table-danger';
    },
    onUploadState(uploading) {
      this.isUploading = !!uploading;
    },
    onTransformedExcel(payload, uploadedName) {
      const data = payload || {};
      this.rows = Array.isArray(data.rows) ? data.rows : [];
      this.fileName = data.fileName || uploadedName || '';
      this.currentPage = 1;
      this.showOnlyErrors = false;
    },
    onClearExcel() {
      this.rows = [];
      this.fileName = '';
      this.currentPage = 1;
      this.showOnlyErrors = false;
    },
    removeRow(item) {
      if (this.isProcessing || !item) return;
      const index = this.rows.indexOf(item);
      if (index > -1) this.rows.splice(index, 1);
    },
    removeAllErrorRows() {
      if (this.isProcessing) return;
      const removed = this.errorRows.length;
      this.rows = this.validRows;
      this.showOnlyErrors = false;
      this.currentPage = 1;
      this.makeToastSuccess(`Đã xoá ${removed} dòng lỗi.`);
    },

    onConfirmImport() {
      if (!this.canCommit) return;
      this.$refs.confirmModal.show();
    },
    doCommitImport() {
      return this.withSubmitLock('commit', async () => {
        const payload = {
          groups: this.groups.map((group) => ({
            senderStoreId: group.senderStoreId,
            receiverStoreId: group.receiverStoreId,
            summary: group.summary,
            products: group.products.map((product) => ({
              productId: product.productId,
              quantity: product.quantity,
            })),
          })),
        };

        try {
          const { data } = await ApiService.post(
            'v2/stock-transfer-imports/commit',
            payload,
          );
          if (data.status !== 200) {
            this.makeToastFaile(data.message);
            return;
          }
          this.makeToastSuccess(
            data.message ||
              `Đã tạo ${data.data.totalTicket} phiếu yêu cầu điều hàng`,
          );
          this.$router.push({ name: 'list-ticket' }).catch(() => {});
        } catch (error) {
          this.handleCommitError(error);
        }
      });
    },

    /**
     * Commit có thể từ chối dòng mà preview đã đánh dấu Hợp lệ: preview chụp dữ liệu lúc tải
     * file, sản phẩm/cửa hàng có thể đổi trạng thái sau đó. BE trả `details.invalidRows` nên
     * đánh dấu ngược lên bảng thay vì chỉ toast — toast tắt là mất manh mối.
     *
     * `productId` rỗng = lỗi của cả phiếu (kho bị xoá) -> đánh dấu mọi dòng cùng cặp kho.
     */
    handleCommitError(error) {
      const body = error && error.response ? error.response.data : null;
      const invalidRows =
        body && body.details && Array.isArray(body.details.invalidRows)
          ? body.details.invalidRows
          : [];

      if (!invalidRows.length) {
        handleError(error);
        return;
      }

      let marked = 0;
      this.rows.forEach((row, index) => {
        if (!row.isValid) return;
        const hit = invalidRows.find(
          (item) =>
            Number(item.senderStoreId) === Number(row.senderStoreId) &&
            Number(item.receiverStoreId) === Number(row.receiverStoreId) &&
            (!item.productId || String(item.productId) === String(row.productId)),
        );
        if (!hit) return;
        // Vue 2: thay cả object để bảng chắc chắn render lại (Rule 9).
        this.$set(this.rows, index, {
          ...row,
          isValid: false,
          errorCode: hit.errorCode,
          errorMessage: hit.errorMessage,
        });
        marked += 1;
      });

      this.currentPage = 1;
      this.showOnlyErrors = marked > 0;
      this.makeToastFaile(
        body.message || `${marked} dòng không còn hợp lệ — đã đánh dấu lại trên bảng`,
      );
    },

    goBack() {
      this.$router.push({ name: 'list-ticket' }).catch(() => {});
    },
  },
};
</script>

<style scoped>
.preview-table >>> td,
.preview-table >>> th {
  vertical-align: middle;
  font-size: 0.9rem;
}
.summary-text {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.raw-hint {
  font-size: 0.75rem;
  line-height: 1.1;
}
.preview-head {
  gap: 0.5rem;
}
.group-note {
  font-size: 0.9rem;
  padding: 0.5rem 0.75rem;
}
</style>
